import { createSlice } from '@reduxjs/toolkit';
import { findWindows } from 'windows-iana';

const RECENTLY_VIEWED_KEYS = ['deals', 'claims', 'clients', 'prospects'];

const loadFilters = () => {
	const storageValue = localStorage.getItem('userPrefs');
	let parsedJson = {};
	try {
		parsedJson = storageValue ? JSON.parse(storageValue) : {};
	} catch (e) {
		localStorage.removeItem('userPrefs');
	}
	const timezone = { iana: '', windows: '' };
	if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
		const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const windowsTimezone = findWindows(userTimezone);
		timezone.iana = userTimezone;
		timezone.windows = windowsTimezone?.[0] ?? '';
	}
	const automationsView =
		parsedJson?.automationsView === 'table' || parsedJson?.automationsView === 'cards'
			? parsedJson.automationsView
			: 'cards';

	const themeMode =
		parsedJson?.themeMode === 'dark' || parsedJson?.themeMode === 'light'
			? parsedJson.themeMode
			: typeof window !== 'undefined' &&
				  window.matchMedia?.('(prefers-color-scheme: dark)').matches
				? 'dark'
				: 'light';

	const invertSideNavColors = Boolean(parsedJson?.invertSideNavColors);

	// Drop items viewed under their old HubSpot ids; they're re-added under the doc id.
	const recentlyViewed = Object.fromEntries(
		Object.entries(parsedJson?.recentlyViewed ?? {}).map(([userId, views]) => [
			userId,
			{
				...views,
				...Object.fromEntries(
					RECENTLY_VIEWED_KEYS.map((key) => [
						key,
						(views?.[key] ?? []).filter((item) => !/^\d+$/.test(String(item?.id))),
					]),
				),
			},
		]),
	);

	return {
		groupView: false,
		kanbanView: false,
		expandPolicies: false,
		...(parsedJson ?? {}),
		recentlyViewed,
		automationsView,
		themeMode,
		invertSideNavColors,
		timezone,
		permissions: {
			dealIds: [],
			clientIds: [],
			ticketIds: [],
		},
	};
};

const updateUserPrefs = (state) => {
	const {
		groupView,
		kanbanView,
		expandPolicies,
		recentlyViewed,
		timezone,
		automationsView,
		themeMode,
		invertSideNavColors,
	} = state;
	localStorage.setItem(
		'userPrefs',
		JSON.stringify({
			groupView,
			kanbanView,
			expandPolicies,
			recentlyViewed,
			timezone,
			automationsView,
			themeMode,
			invertSideNavColors,
		}),
	);
};

export const userPrefsSlice = createSlice({
	name: 'userPrefs',
	initialState: loadFilters(),
	reducers: {
		setGroupView: (state, action) => {
			console.log('👾 ~ setGroupView: action.payload', action.payload);
			state.groupView = action.payload;

			updateUserPrefs(state);
		},
		setKanbanView: (state, action) => {
			console.log('👾 ~ setKanbanView: action.payload', action.payload);
			state.kanbanView = action.payload;

			updateUserPrefs(state);
		},
		setExpandPolicies: (state, action) => {
			console.log('👾 ~ setKanbanView: action.payload', action.payload);
			state.expandPolicies = action.payload;

			updateUserPrefs(state);
		},
		setAutomationsView: (state, action) => {
			state.automationsView = action.payload === 'table' ? 'table' : 'cards';
			updateUserPrefs(state);
		},
		setThemeMode: (state, action) => {
			state.themeMode = action.payload === 'dark' ? 'dark' : 'light';
			updateUserPrefs(state);
		},
		setInvertSideNavColors: (state, action) => {
			state.invertSideNavColors = Boolean(action.payload);
			updateUserPrefs(state);
		},
		addPermission: (state, action) => {
			const { type, id } = action.payload;
			const existing = state.permissions[type];
			state.permissions[type] = Array.from(
				new Set([...existing.map((r) => r?.toString()), id.toString()]),
			);
		},
		clearPermissions: (state) => {
			state.permissions = {
				dealIds: [],
				ticketIds: [],
				clientIds: [],
			};
		},
		addRecentlyViewed: (state, action) => {
			const { userId, type, data } = action.payload;
			if (!RECENTLY_VIEWED_KEYS.includes(type)) return;

			const { id, name, path, isProspect } = data ?? {};
			const newTime = Date.now();

			const existingUserViews = {
				clients: [],
				deals: [],
				claims: [],
				...(state.recentlyViewed[userId] ?? {}),
			};
			let list = existingUserViews[type] ?? [];

			list = list.filter((item) => item.id !== id);
			list.unshift({ id, name, path, time: newTime, ...(isProspect && { isProspect }) });

			list.sort((a, b) => b.time - a.time);
			list = list.slice(0, 10);

			state.recentlyViewed[userId] = { ...existingUserViews, [type]: list };

			updateUserPrefs(state);
		},
		removeRecentlyViewed: (state, action) => {
			const { userId, type, id } = action.payload;
			if (!userId || !id) return;
			if (!RECENTLY_VIEWED_KEYS.includes(type)) return;

			const existingUserViews = state.recentlyViewed[userId];
			if (!existingUserViews || !existingUserViews[type]) return;

			const updatedList = existingUserViews[type].filter((item) => item.id !== id);

			state.recentlyViewed[userId] = {
				...existingUserViews,
				[type]: updatedList,
			};

			updateUserPrefs(state);
		},
	},
});

export const {
	setGroupView,
	setKanbanView,
	setExpandPolicies,
	setAutomationsView,
	setThemeMode,
	setInvertSideNavColors,
	addPermission,
	clearPermissions,
	addRecentlyViewed,
	removeRecentlyViewed,
} = userPrefsSlice.actions;

export default userPrefsSlice.reducer;
